import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import TimeToLeaveIcon from "@mui/icons-material/TimeToLeave";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../../components/MainContainer";
import { headerDataMission } from "../../../data/modules/mission-header";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import Message from "../../tools/Message";
import MissionData from "../Timesheet/MissionData";
import TransportData from "../fuel/TransportData";
import DecompteMissionService from "../../../services/DecompteMissionService";
import MissionTransportService from "../../../services/MissionTransportService";
import decompteValidationUseStyles from "../../../styles/DecompteValidation.style";

const formaterDate = (valeur) => {
  if (!valeur) return "-";
  const date = new Date(valeur);
  return Number.isNaN(date.getTime()) ? valeur : date.toLocaleDateString();
};

const formaterPeriode = (debut, fin) => {
  if (!debut && !fin) return "-";
  const debutFormate = formaterDate(debut);
  const finFormatee = formaterDate(fin);
  return debutFormate === finFormatee
    ? debutFormate
    : `${debutFormate} - ${finFormatee}`;
};

const calculerDistanceParcourue = (destination = {}) => {
  const depart = Number.parseFloat(destination.km_depart) || 0;
  const arrivee = Number.parseFloat(destination.km_arrivee) || 0;
  return Math.max(0, arrivee - depart);
};

const calculerTotaux = (destinations = []) =>
  destinations.reduce(
    (totaux, destination) => ({
      distancePrevisionnelle:
        totaux.distancePrevisionnelle +
        (Number.parseFloat(destination.distance_km) || 0),
      distanceReelle:
        totaux.distanceReelle +
        (Number.parseFloat(
          destination.distance_parcourue ??
            calculerDistanceParcourue(destination),
        ) || 0),
      tempsEntreSites:
        totaux.tempsEntreSites +
        (Number.parseFloat(destination.between_site_duration_min) || 0),
      tempsSurSite:
        totaux.tempsSurSite +
        (Number.parseFloat(destination.on_site_duration_min) || 0),
    }),
    {
      distancePrevisionnelle: 0,
      distanceReelle: 0,
      tempsEntreSites: 0,
      tempsSurSite: 0,
    },
  );

const normaliserTransportId = (valeur) => {
  const identifiant = Number(valeur);
  return Number.isFinite(identifiant) && identifiant > 0 ? identifiant : null;
};

const obtenirTransportDestination = (
  destination = {},
  transportDecompteId = null,
) => {
  const transports = Array.isArray(destination.transports)
    ? destination.transports
    : [];
  const identifiantDestination = normaliserTransportId(destination.transport_id);
  if (identifiantDestination) {
    return (
      transports.find(
        (transport) =>
          normaliserTransportId(transport?.transport_id) ===
          identifiantDestination,
      ) || { transport_id: identifiantDestination }
    );
  }

  const identifiantDecompte = normaliserTransportId(transportDecompteId);
  if (identifiantDecompte) {
    return transports.find(
      (transport) => normaliserTransportId(transport?.transport_id) === identifiantDecompte,
    ) || { transport_id: identifiantDecompte };
  }

  return (
    transports.find(
      (transport) =>
        normaliserTransportId(transport?.transport_id) ===
        identifiantDestination,
    ) ||
    transports[0] ||
    (identifiantDestination ? { transport_id: identifiantDestination } : null)
  );
};

const construireLibelleTransport = (
  transport = {},
  destination = {},
  transportId = null,
) => {
  const typeTransport =
    destination.transport_type ||
    destination.rent_type ||
    transport.vhc_type ||
    "Transport";
  const estLocation =
    Boolean(destination.mission_rent_id) ||
    Boolean(transport.mission_rent_id) ||
    /location/i.test(typeTransport);
  const mode = estLocation
    ? destination.rent_type || typeTransport || "Location"
    : "Voiture";
  const reference =
    transport.vhc_imm ||
    destination.vhc_imm ||
    transport.vhc_type ||
    typeTransport ||
    (transportId ? "Transport " + transportId : "Sans transport");

  return mode + " - " + reference;
};

const TABLE_COLUMNS_COUNT = 24;
const DecompteValidationDetail = () => {
  const classes = decompteValidationUseStyles();
  const navigate = useNavigate();
  const { mission_decompte_id: missionDecompteId } = useParams();
  const [chargement, setChargement] = useState(true);
  const [traitement, setTraitement] = useState(false);
  const [decompte, setDecompte] = useState(null);
  const [mission, setMission] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [transports, setTransports] = useState([]);
  const [transportDetailDialog, setTransportDetailDialog] = useState({
    open: false,
    transport: null,
  });
  const [refus, setRefus] = useState({ open: false, observation: "" });
  const [groupesReduits, setGroupesReduits] = useState(() => new Set());
  const [message, setMessage] = useState({
    open: false,
    color: "green",
    messageInfo: "",
  });

  useEffect(() => {
    let actif = true;

    const chargerDetail = async () => {
      const id = Number(missionDecompteId);
      if (id <= 0) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: "Identifiant de decompte invalide.",
        });
        setChargement(false);
        return;
      }

      setChargement(true);
      const response =
        await DecompteMissionService.getAccountingDecompteDetail(id);
      if (!actif) return;

      if (!response?.status) {
        setMessage({
          open: true,
          color: "red",
          messageInfo:
            response?.message || "Decompte introuvable ou deja traite.",
        });
        setChargement(false);
        return;
      }

      setDecompte(response.data?.decompte || null);
      setMission(response.data?.mission || null);
      setDestinations(
        Array.isArray(response.data?.destinations)
          ? response.data.destinations
          : [],
      );
      const decompteData = response.data?.decompte;
      if (decompteData?.mission_planning_id && decompteData?.mission_planning_agenda_id) {
        try {
          const transportResponse = await MissionTransportService.transportsByMissionPlanning(
            decompteData.mission_planning_id,
            decompteData.mission_planning_agenda_id,
          );
          if (actif && transportResponse?.status) {
            setTransports(Array.isArray(transportResponse.data) ? transportResponse.data : []);
          }
        } catch (error) {
          if (actif) setTransports([]);
        }
      }
      setChargement(false);
    };

    chargerDetail();
    return () => {
      actif = false;
    };
  }, [missionDecompteId]);

  const totaux = useMemo(() => calculerTotaux(destinations), [destinations]);
  const groupesDestinations = useMemo(() => {
    const groupes = new Map();
    const ordre = [];
    const transportsParId = new Map(
      transports.map((transport) => [normaliserTransportId(transport.transport_id), transport]),
    );

    destinations.forEach((destination, index) => {
      const transportDestination = obtenirTransportDestination(
        destination,
        decompte?.transport_id,
      );
      const transportId = normaliserTransportId(transportDestination?.transport_id);
      const transport = transportsParId.get(transportId) || transportDestination;
      const cle = transportId ? "transport_" + transportId : "sans_transport";

      if (!groupes.has(cle)) {
        groupes.set(cle, {
          cle,
          libelle: transportId
            ? construireLibelleTransport(transport, destination, transportId)
            : "Sans transport",
          transport,
          lignes: [],
        });
        ordre.push(cle);
      }

      groupes.get(cle).lignes.push({ destination, index });
    });

    return ordre.map((cle) => groupes.get(cle));
  }, [decompte?.transport_id, destinations, transports]);
  const basculerGroupe = (cleGroupe) => {
    setGroupesReduits((precedents) => {
      const suivants = new Set(precedents);

      if (suivants.has(cleGroupe)) {
        suivants.delete(cleGroupe);
      } else {
        suivants.add(cleGroupe);
      }

      return suivants;
    });
  };

  const estEnAttente = decompte?.mission_decompte_state === "PENDING";
  const statutDecompte = estEnAttente
    ? {
        label: "En attente de validation",
        color: "purple",
      }
    : {
        label: "Confirm\u00e9",
        color: "green",
      };

  const validerDecompte = async () => {
    setTraitement(true);
    try {
      const response =
        await DecompteMissionService.validateDecompte(missionDecompteId);
      if (!response?.status) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: response?.message || "Validation impossible.",
        });
        return;
      }
      navigate("/accounting/decompte", { replace: true });
    } catch (error) {
      setMessage({
        open: true,
        color: "red",
        messageInfo: error.message || "Erreur lors de la validation.",
      });
    } finally {
      setTraitement(false);
    }
  };

  const refuserDecompte = async () => {
    const observation = refus.observation.trim();
    if (!observation) {
      setMessage({
        open: true,
        color: "warning",
        messageInfo: "Veuillez saisir une observation.",
      });
      return;
    }

    setTraitement(true);
    try {
      const response = await DecompteMissionService.refuseAccountingDecompte(
        missionDecompteId,
        observation,
      );
      if (!response?.status) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: response?.message || "Refus impossible.",
        });
        return;
      }
      navigate("/accounting/decompte", { replace: true });
    } catch (error) {
      setMessage({
        open: true,
        color: "red",
        messageInfo: error.message || "Erreur lors du refus.",
      });
    } finally {
      setTraitement(false);
    }
  };

  const actionsDesactivees =
    chargement || traitement || !decompte || !estEnAttente;

  return (
    <MainContainer {...headerDataMission}>
      <LoadingSimpleBackdrop
        openLoadingSimpleBackdrop={chargement || traitement}
      />
      <Message
        open={message.open}
        color={message.color}
        messageInfo={message.messageInfo}
        handleClose={() =>
          setMessage((precedent) => ({ ...precedent, open: false }))
        }
      />

      <Dialog
        open
        fullScreen
        aria-labelledby="form-dialog-title"
        aria-describedby="decompte-validation-content"
        className={classes.detailDialog}
      >
        <DialogTitle id="form-dialog-title" sx={{ color: "#0f4c81", fontWeight: 700 }}>
          <Box className={classes.detailDialogTitle}>
            <Box>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {estEnAttente
                  ? "Validation du décompte"
                  : "Consultation du décompte"}
              </Box>
              {decompte?.reference && (
                <Box
                  component="span"
                  sx={{ ml: 1, color: "text.secondary", fontSize: 14 }}
                >
                  - {decompte.reference}
                </Box>
              )}
            </Box>
            {decompte && (
              <Chip
                label={statutDecompte.label}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "#ffffff",
                  backgroundColor: "#ffffff",
                  color: statutDecompte.color,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers id="decompte-validation-content">
          <DialogContentText
            component="div"
            sx={{ fontSize: "12px", color: "#8e9194" }}
          >
            {!chargement && !decompte && (
              <Alert severity="warning">
                {"Ce d\u00e9compte est introuvable ou non autoris\u00e9."}
              </Alert>
            )}

            {decompte && (
              <>
                {mission && (
                  <Box sx={{ "& .MuiTextField-root": { mb: 0.5 }, "& .MuiTableCell-root": { py: 0.5, fontSize: "0.78rem" } }}>
                    <MissionData mission={mission} />
                  </Box>
                )}
                
                <fieldset className={classes.fieldset}>
                  <legend>{decompte.reference || "Décompte de mission"}</legend>
                  {destinations.length === 0 ? (
                    <Alert severity="info">
                      Aucune donnée de décompte disponible.
                    </Alert>
                  ) : (
                    <TableContainer
                      className={classes.tableContainer}
                      sx={{
                        width: "100%",
                        maxHeight: "64vh",
                        overflow: "auto",
                        border: "1px solid #d7e3ee",
                        borderRadius: 2,
                        boxShadow: "0 4px 14px rgba(15, 76, 129, 0.08)",
                      }}
                    >
                      <Table
                        stickyHeader
                        size="small"
                        className={classes.detailTable}
                        sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                      >
                        <TableHead>
                          <TableRow
                            sx={{
                              "& .MuiTableCell-root": {
                                backgroundColor: "#eff6ff",
                                color: "#0f4c81",
                                fontWeight: 700,
                                borderColor: "#c9d9e8",
                              },
                            }}
                          >
                            <TableCell
                              rowSpan={2}
                              sx={{ maxWidth: 100, textAlign: "center" }}
                            >
                              Date(s)
                            </TableCell>
                            <TableCell
                              colSpan={5}
                              sx={{ minWidth: 210, textAlign: "center" }}
                            >
                              {"Lieu de d\u00e9part"}
                            </TableCell>
                            <TableCell
                              colSpan={5}
                              sx={{ minWidth: 210, textAlign: "center" }}
                            >
                              {"Lieu d'arriv\u00e9e"}
                            </TableCell>
                            <TableCell
                              rowSpan={2}
                              sx={{ minWidth: 60, textAlign: "center" }}
                            >
                              Type
                            </TableCell>
                            <TableCell
                              rowSpan={2}
                              sx={{ minWidth: 60, textAlign: "center" }}
                            >
                              Financement
                            </TableCell>
                            <TableCell
                              rowSpan={2}
                              sx={{ minWidth: 240, textAlign: "center" }}
                            >
                              Moyen de transport
                            </TableCell>
                            <TableCell
                              colSpan={2}
                              sx={{ minWidth: 60, textAlign: "center" }}
                            >
                              {"Odom\u00e8tre"}
                            </TableCell>
                            <TableCell
                              colSpan={2}
                              sx={{ minWidth: 60, textAlign: "center" }}
                            >
                              Distance Parcourue
                            </TableCell>
                            <TableCell
                              colSpan={3}
                              sx={{ minWidth: 130, textAlign: "center" }}
                            >
                              {"Temps pass\u00e9 entre deux sites"}
                            </TableCell>
                            <TableCell
                              colSpan={3}
                              sx={{ minWidth: 130, textAlign: "center" }}
                            >
                              {"Temps pass\u00e9 sur Site / Bureau"}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              "& .MuiTableCell-root": {
                                backgroundColor: "#eff6ff",
                                color: "#0f4c81",
                                fontWeight: 700,
                                borderColor: "#c9d9e8",
                              },
                            }}
                          >
                            {[
                              "R\u00e9gion",
                              "District",
                              "Commune",
                              "Fokontany",
                              "Lieu",
                            ].map((titre, index) => (
                              <TableCell
                                key={"depart-" + titre}
                                sx={{
                                  minWidth: 70,
                                  textAlign: "center",
                                  borderLeft:
                                    index === 0
                                      ? "1px solid #c8c8c8ff"
                                      : undefined,
                                }}
                              >
                                {titre}
                              </TableCell>
                            ))}
                            {[
                              "R\u00e9gion",
                              "District",
                              "Commune",
                              "Fokontany",
                              "Lieu",
                            ].map((titre, index) => (
                              <TableCell
                                key={"arrivee-" + titre}
                                sx={{
                                  minWidth: 70,
                                  textAlign: "center",
                                  borderLeft:
                                    index === 0
                                      ? "1px solid #c8c8c8ff"
                                      : undefined,
                                }}
                              >
                                {titre}
                              </TableCell>
                            ))}
                            <TableCell
                              sx={{
                                minWidth: 140,
                                textAlign: "center",
                                borderLeft: "1px solid #c8c8c8ff",
                              }}
                            >
                              {"D\u00e9part"}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 140, textAlign: "center" }}
                            >
                              {"Arriv\u00e9e"}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: 60,
                                textAlign: "center",
                                borderLeft: "1px solid #c8c8c8ff",
                              }}
                            >
                              {"Pr\u00e9visionnelle"}
                            </TableCell>
                            <TableCell sx={{ minWidth: 60 }}>
                              {"R\u00e9elle"}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: 150,
                                textAlign: "center",
                                borderLeft: "1px solid #c8c8c8ff",
                              }}
                            >
                              {"Heure D\u00e9part"}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 150, textAlign: "center" }}
                            >
                              {"Heure Arriv\u00e9e"}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 150, textAlign: "center" }}
                            >
                              {"Dur\u00e9e en mn"}
                            </TableCell>
                            <TableCell
                              sx={{
                                minWidth: 150,
                                textAlign: "center",
                                borderLeft: "1px solid #c8c8c8ff",
                              }}
                            >
                              {"Heure Arriv\u00e9e"}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 150, textAlign: "center" }}
                            >
                              {"Heure D\u00e9part"}
                            </TableCell>
                            <TableCell
                              sx={{ minWidth: 150, textAlign: "center" }}
                            >
                              {"Dur\u00e9e en mn"}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody
                          sx={{
                            "& .MuiTableCell-body": {
                              fontSize: "12px",
                            },
                          }}
                        >
                          {groupesDestinations.map((groupe) => (
                            <React.Fragment key={groupe.cle}>
                              <TableRow
                                className={classes.transportGroupRow}
                                sx={{
                                  backgroundColor: "#e5f0f8",
                                  "& .MuiTableCell-root": { fontWeight: 600, py: 1 },
                                }}
                                tabIndex={0}
                                role="button"
                                aria-expanded={!groupesReduits.has(groupe.cle)}
                                onClick={() => basculerGroupe(groupe.cle)}
                                onKeyDown={(event) => {
                                  if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                  ) {
                                    event.preventDefault();
                                    basculerGroupe(groupe.cle);
                                  }
                                }}
                              >
                                <TableCell colSpan={TABLE_COLUMNS_COUNT}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                      <Box component="span" aria-hidden="true" sx={{ width: 18, color: "#1976d2", fontSize: 16, lineHeight: 1 }}>
                                        {groupesReduits.has(groupe.cle) ? "\u25b8" : "\u25be"}
                                      </Box>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ color: "#0f4c81", fontWeight: 800, fontSize: "0.9rem" }}>
                                          {groupe.libelle}
                                        </Typography>
                                        {groupe.transport?.driver_name && (
                                          <Typography variant="caption" color="text.secondary">
                                            Chauffeur : {groupe.transport.driver_name}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Chip
                                        label={`${groupe.lignes.length} ${groupe.lignes.length > 1 ? "destinations" : "destination"}`}
                                        size="small"
                                        variant="outlined"
                                        color="info"
                                      />
                                      {groupe.transport && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="info"
                                          startIcon={<InfoOutlinedIcon />}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setTransportDetailDialog({ open: true, transport: groupe.transport });
                                          }}
                                        >
                                          Détails
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {!groupesReduits.has(groupe.cle) &&
                                groupe.lignes.map(({ destination, index }) => (
                                  <TableRow
                                    key={
                                      destination.mission_decompte_detail_id ||
                                      groupe.cle + "-" + index
                                    }
                                    sx={{
                                      "&:nth-of-type(odd)": {
                                        backgroundColor: "#ffffffdb",
                                      },
                                      "&:hover": {
                                        backgroundColor: "#f3f7fbeb",
                                      },
                                    }}
                                  >
                                    <TableCell sx={{ maxWidth: 100 }}>
                                      {formaterPeriode(
                                        destination.date_begin,
                                        destination.date_end,
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        borderLeft: "1px solid #c8c8c8ff",
                                        py: 0.5,
                                      }}
                                    >
                                      {destination.region_depart || "-"}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                                      {destination.district_depart || "-"}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                                      {destination.commune_depart || "-"}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                                      {destination.fokontany_depart || "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.lieu_depart ||
                                        destination.direction_location ||
                                        "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        borderLeft: "1px solid #c8c8c8ff",
                                        py: 0.5,
                                      }}
                                    >
                                      {destination.region_arrivee || "-"}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                                      {destination.district_arrivee || "-"}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                                      {destination.commune_arrivee || "-"}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 120, py: 0.5 }}>
                                      {destination.fokontany_arrivee || "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 140,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.lieu_arrivee ||
                                        destination.direction_location ||
                                        "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderLeft: "1px solid #e6e6e6",
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.type === "DM" ? (
                                        <Chip
                                          icon={<CardTravelIcon />}
                                          label="Mission"
                                          variant="outlined"
                                          size="small"
                                        />
                                      ) : (
                                        <Chip
                                          icon={<TimeToLeaveIcon />}
                                          label={"D\u00e9lai de route"}
                                          variant="outlined"
                                          size="small"
                                          sx={{ fontSize: "12px" }}
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 80, textAlign: "center" }}
                                    >
                                      {destination.code_financement_mis || "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 140,
                                        textAlign: "center",
                                        borderLeft: "1px solid #c8c8c8ff",
                                      }}
                                    >
                                      {destination.transport_type || "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 140,
                                        textAlign: "center",
                                        borderLeft: "1px solid #c8c8c8ff",
                                      }}
                                    >
                                      {destination.km_depart ?? "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 140,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.km_arrivee ?? "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 140,
                                        textAlign: "center",
                                        borderLeft: "1px solid #c8c8c8ff",
                                      }}
                                    >
                                      {destination.distance_km ?? "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 80, textAlign: "center" }}
                                    >
                                      {destination.distance_parcourue ??
                                        calculerDistanceParcourue(destination)}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                        borderLeft: "1px solid #c8c8c8ff",
                                      }}
                                    >
                                      {destination.between_site_start_hour ||
                                        destination.heure_begin ||
                                        "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.between_site_end_hour ||
                                        destination.heure_end ||
                                        "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.between_site_duration_min ||
                                        0}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                        borderLeft: "1px solid #c8c8c8ff",
                                      }}
                                    >
                                      {destination.on_site_arrivee_hour ||
                                        destination.heure_end ||
                                        "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.on_site_depart_hour ||
                                        destination.heure_begin ||
                                        "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        minWidth: 120,
                                        textAlign: "center",
                                      }}
                                    >
                                      {destination.on_site_duration_min || 0}
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </React.Fragment>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TableCell colSpan={14} sx={{ fontWeight: 700 }}>
                              Total
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="center">
                              {totaux.distancePrevisionnelle.toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              {totaux.distanceReelle.toFixed(2)}
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="center">
                              {totaux.tempsEntreSites.toFixed(2)}
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="center">
                              {totaux.tempsSurSite.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  )}
                </fieldset>
              </>
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions className={classes.detailDialogActions}>
          <Box className={classes.dialogScrollNotice}>
            <strong>
              {
                "Veuillez d\u00e9filer horizontalement le tableau pour voir toutes les colonnes."
              }
            </strong>
          </Box>
          <Box className={classes.dialogActionButtons}>
            <Button
              variant="contained"
              color={estEnAttente ? "error" : "info"}
              size="medium"
              onClick={() => navigate("/accounting/decompte")}
            >
              {estEnAttente ? "Annuler" : "Retour"}
            </Button>
            {estEnAttente && (
              <>
                <Button
                  variant="contained"
                  color="error"
                  size="medium"
                  startIcon={<CloseIcon />}
                  disabled={actionsDesactivees}
                  onClick={() => setRefus({ open: true, observation: "" })}
                >
                  Refuser
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  size="medium"
                  startIcon={<CheckCircleOutlineIcon />}
                  disabled={actionsDesactivees}
                  onClick={validerDecompte}
                >
                  Valider
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transportDetailDialog.open}
        onClose={() => setTransportDetailDialog({ open: false, transport: null })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ color: "#0f4c81", fontWeight: 700 }}>
          Détails du transport
        </DialogTitle>
        <DialogContent dividers>
          {transportDetailDialog.transport && (
            <>
              <TransportData transport={transportDetailDialog.transport} />
              {transportDetailDialog.transport.mission_rent_id && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 2, backgroundColor: "#f5f9fd" }}>
                  <Typography variant="subtitle2" sx={{ color: "#0f4c81", fontWeight: 700, mb: 0.5 }}>
                    Location
                  </Typography>
                  <Typography variant="body2">
                    {transportDetailDialog.transport.rent_type || "Location"}
                  </Typography>
                  <Typography variant="body2">
                    Tarif : {transportDetailDialog.transport.rent_amount ?? "—"} Ar
                  </Typography>
                  <Typography variant="body2">
                    Mode de paiement : {transportDetailDialog.transport.rent_paiement || "—"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setTransportDetailDialog({ open: false, transport: null })}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refus.open}
        onClose={() =>
          !traitement && setRefus({ open: false, observation: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Refuser le decompte</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Ajoutez une observation qui sera transmise au chef de bord.
          </DialogContentText>
          <TextField
            label="Observation"
            fullWidth
            multiline
            minRows={3}
            value={refus.observation}
            onChange={(event) =>
              setRefus((precedent) => ({
                ...precedent,
                observation: event.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={traitement}
            onClick={() => setRefus({ open: false, observation: "" })}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={traitement}
            onClick={refuserDecompte}
          >
            Confirmer le refus
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default DecompteValidationDetail;
